import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnalyzerWeeklyPrompt } from '@Models/types';
import {
  CaretUp,
  CheckCircle,
  MagnifyingGlass,
  Plus,
  X,
} from '@phosphor-icons/react';
import { useAuth } from '@Context/CognitoAuthProvider';
import { ArtilleryAPI } from '@Lib/axios/artillery';
import { CreatePromptModal } from './prompts/CreatePromptModal';
import { EditPromptModal } from './prompts/EditPromptModal';
import { ViewPromptModal } from './prompts/ViewPromptModal';
import { DeletePromptModal } from './prompts/DeletePromptModal';

const ArtilleryPromptDropdown: React.FC = () => {
  const { logout, user } = useAuth();
  const api = useMemo(() => ArtilleryAPI(logout), [logout]);

  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [settingDefault, setSettingDefault] = useState(false);
  const [prompts, setPrompts] = useState<AnalyzerWeeklyPrompt[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedPrompt, setSelectedPrompt] =
    useState<AnalyzerWeeklyPrompt | null>(null);
  const isAdmin = !!user?.admin;

  // Modal state
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const fetchPrompts = async () => {
    setLoading(true);
    try {
      const data = await api.getPrompts();
      setPrompts(data);
      const def = data.find((p) => p.isDefault) || null;
      setSelectedPrompt(def || data[0] || null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrompts();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
        setSearchTerm('');
      }
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const filtered =
    isAdmin && searchTerm
      ? prompts.filter((p) =>
          p.promptTitle.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : prompts;

  const openCreate = () => {
    setCreateOpen(true);
    setOpen(false);
  };

  const openView = (p: AnalyzerWeeklyPrompt) => {
    setSelectedPrompt(p);
    setViewOpen(true);
    setOpen(false);
  };

  const openEdit = (p: AnalyzerWeeklyPrompt) => {
    setSelectedPrompt(p);
    setEditOpen(true);
    setViewOpen(false);
    setOpen(false);
  };

  const openDelete = (p: AnalyzerWeeklyPrompt) => {
    setSelectedPrompt(p);
    setDeleteOpen(true);
    setViewOpen(false);
    setOpen(false);
  };

  const closeAll = () => {
    setCreateOpen(false);
    setEditOpen(false);
    setViewOpen(false);
    setDeleteOpen(false);
  };

  const handleCreate = async (
    prompt: Omit<
      AnalyzerWeeklyPrompt,
      '_id' | 'authorEmail' | 'dateCreated' | 'isDefault'
    >
  ) => {
    try {
      await api.createPrompt(prompt);
      await fetchPrompts();
      closeAll();
    } catch (error) {
      console.error('Error creating prompt:', error);
    }
  };

  const handleUpdate = async (
    originalTitle: string,
    originalAuthor: string,
    prompt: Partial<AnalyzerWeeklyPrompt>
  ) => {
    try {
      await api.updatePrompt(originalTitle, originalAuthor, prompt);
      await fetchPrompts();
      closeAll();
    } catch (error) {
      console.error('Error updating prompt:', error);
    }
  };

  const handleDelete = async (promptTitle: string, authorEmail: string) => {
    try {
      await api.deletePrompt(promptTitle, authorEmail);
      await fetchPrompts();
      closeAll();
    } catch (error) {
      console.error('Error deleting prompt:', error);
    }
  };

  const handleSetDefault = async (
    e: React.MouseEvent,
    p: AnalyzerWeeklyPrompt
  ) => {
    e.stopPropagation();
    setSettingDefault(true);
    try {
      await api.setDefaultPrompt(p.promptTitle, p.authorEmail);
      await fetchPrompts();
    } finally {
      setSettingDefault(false);
    }
  };

  return (
    <div className="relative w-72" ref={dropdownRef}>
      <button
        type="button"
        className="flex w-full items-center justify-between rounded-md border bg-white px-4 py-2 font-inter text-sm focus:outline-none focus:ring-0"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={loading || settingDefault}
      >
        <span className="flex items-center gap-2 whitespace-nowrap">
          {settingDefault ? (
            <>
              <div className="h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-green-500" />
              Setting default...
            </>
          ) : prompts.length > 0 ? (
            (() => {
              const def = prompts.find((p) => p.isDefault);
              return def ? (
                <>
                  <CheckCircle
                    size={12}
                    weight="fill"
                    className="text-green-500"
                  />
                  {def.promptTitle}
                  <span className="text-xs text-green-600">(Default)</span>
                </>
              ) : (
                prompts[0]?.promptTitle || 'Select Prompt'
              );
            })()
          ) : (
            'No Prompts Available'
          )}
        </span>
        <CaretUp
          size={18}
          className={`ml-2 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <ul
          className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border bg-white py-1 shadow-lg focus:outline-none"
          tabIndex={-1}
          role="listbox"
        >
          {isAdmin && (
            <>
              <li className="px-4 py-2">
                <div className="flex w-full flex-row items-center gap-x-2 rounded-md border px-2 py-1">
                  <MagnifyingGlass size={16} weight="bold" />
                  <input
                    type="text"
                    className="w-full border-gray-300 font-inter text-xs focus:outline-none sm:text-sm"
                    placeholder="Search prompts..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  {searchTerm && (
                    <span
                      className="cursor-pointer hover:text-brandRed-500"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSearchTerm('');
                      }}
                    >
                      <X size={16} weight="bold" />
                    </span>
                  )}
                </div>
              </li>
              <li className="border-b border-gray-200">
                <button
                  className="flex w-full items-center gap-2 px-4 py-2 text-sm text-primary-600 hover:bg-primary-50"
                  onClick={(e) => {
                    e.stopPropagation();
                    openCreate();
                  }}
                >
                  <Plus size={16} weight="bold" />
                  New Prompt
                </button>
              </li>
            </>
          )}
          {filtered.length === 0 && (
            <li className="flex cursor-pointer items-center justify-between px-4 py-2 text-sm hover:bg-primary-100">
              <span className="flex w-full flex-row items-center justify-between">
                <span>
                  {isAdmin && searchTerm
                    ? 'No prompts match your search'
                    : 'No Prompts Available'}
                </span>
              </span>
            </li>
          )}
          {filtered.map((prompt) => (
            <li
              key={`${prompt.promptTitle}-${prompt.authorEmail}`}
              className={`group relative flex cursor-pointer items-center justify-between px-4 py-2 text-sm hover:bg-primary-100 ${
                prompt.promptTitle === selectedPrompt?.promptTitle &&
                prompt.authorEmail === selectedPrompt?.authorEmail
                  ? 'bg-primary-50 font-semibold'
                  : ''
              }`}
              onClick={() => openView(prompt)}
              role="option"
              aria-selected={
                prompt.promptTitle === selectedPrompt?.promptTitle &&
                prompt.authorEmail === selectedPrompt?.authorEmail
              }
            >
              <span className="flex items-center gap-2">
                {prompt.isDefault && (
                  <CheckCircle
                    size={12}
                    weight="fill"
                    className="text-green-500"
                  />
                )}
                {prompt.promptTitle}
                {prompt.isDefault && (
                  <span className="text-xs text-green-600">(Default)</span>
                )}
              </span>
              {isAdmin && !prompt.isDefault && (
                <button
                  className="invisible rounded border px-2 py-1 text-xs text-primary-700 hover:bg-primary-50 group-hover:visible"
                  onClick={(e) => handleSetDefault(e, prompt)}
                >
                  Set default
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Modals */}
      <CreatePromptModal
        isOpen={createOpen}
        onClose={closeAll}
        onCreatePrompt={handleCreate}
      />

      <EditPromptModal
        isOpen={editOpen}
        onClose={closeAll}
        prompt={selectedPrompt}
        onUpdatePrompt={handleUpdate}
      />

      <ViewPromptModal
        isOpen={viewOpen}
        onClose={closeAll}
        prompt={selectedPrompt}
        isAdmin={isAdmin}
        onEdit={openEdit}
        onDelete={openDelete}
        onSetDefault={(prompt) =>
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          handleSetDefault({ stopPropagation: () => {} } as any, prompt)
        }
        currentDefaultPrompt={prompts.find((p) => p.isDefault) || null}
      />

      <DeletePromptModal
        isOpen={deleteOpen}
        onClose={closeAll}
        prompt={selectedPrompt}
        onDeletePrompt={handleDelete}
      />
    </div>
  );
};

export default ArtilleryPromptDropdown;
